import React, { useState } from 'react';
import '../../../styles/mobile-dashboard.css';
import { useArtist } from '../../../contexts/ArtistContext';
import WeeklyGrowthChart from '../../Charts/WeeklyGrowthChart';
import MonthlyGrowthChart from '../../Charts/MonthlyGrowthChart';
import EngagementRatesChart from '../../Charts/EngagementRatesChart';
import FunnelChart from '../../Charts/FunnelChart';
import InstagramAgeDistributionChart from '../../Charts/InstagramAgeDistributionChart';
import InstagramGenderDistributionChart from '../../Charts/InstagramGenderDistributionChart';
import MonthlyFollowersChart from '../../Charts/MonthlyFollowersChart';
import MonthlyListenersChart from '../../Charts/MonthlyListenersChart';
import EditorialPlaylistMixedChart from '../../Charts/EditorialPlaylistMixedChart';
import TotalPlaylistMixedChart from '../../Charts/TotalPlaylistMixedChart';
import BrandAffinityChart from '../../Charts/BrandAffinityChart';
import PlatformGrowthComparisonChart from '../../Charts/PlatformGrowthComparisonChart';
import GeographicDistributionBarChart from '../../Charts/GeographicDistributionBarChart';
import YouTubeGenderDistributionChart from '../../Charts/YouTubeGenderDistributionChart';
import AudienceReachPyramidChart from '../../Charts/AudienceReachPyramidChart';
import { Report } from '../../../types/dashboard';
import DateRangePicker from '../DateRangePicker';
import ReportCustomizationDropdown from '../ReportCustomizationDropdown';
import MobileFilterBar from './MobileFilterBar';
import MobileHighlights from './MobileHighlights';
import MobileChartCard from './MobileChartCard';

export type MobileDashboardProps = {
  dateRange: { from: Date; to: Date };
  onDateRangeChange: (from: Date, to: Date) => void;
  reports?: Report[];
  onToggleReport?: (reportId: string, isVisible: boolean) => Promise<void>;
  /** Same gate as desktop analytics toolbar for Add/Remove reports. */
  analyticsActionsEnabled?: boolean;
  platformArtistId?: string;
  platformMappingLoading?: boolean;
  platformMappingError?: string | null;
  userPrimaryArtistId?: string;
};

function getWidgetTitle(id: string): string {
  switch (id) {
    case 'monthly-listeners':
      return 'Revenue & Streaming Performance';
    case 'revenue-streaming-performance':
      return 'Revenue & Streaming Performance';
    case 'monthly-followers':
      return 'Monthly Followers';
    case 'editorial-playlists':
      return 'Editorial Playlist Reach';
    case 'editorial-playlist-reach':
      return 'Editorial Playlist Reach';
    case 'platforms':
      return 'Total Playlist Count';
    case 'total-playlist-count':
      return 'Total Playlist Count';
    case 'brand-affinity':
      return 'Brand Affinity';
    case 'platform-growth-comparison':
      return 'Platform Growth Comparison';
    case 'geography':
      return 'Geographic Distribution';
    case 'geographic-distribution':
      return 'Geographic Distribution';
    case 'weekly-growth':
      return 'Weekly Growth';
    case 'monthly-growth':
      return 'Monthly Growth';
    case 'engagement-rates':
    case 'engagement':
      return 'Engagement Rates';
    case 'spotify-funnel':
    case 'funnel':
      return 'Engagement Funnel';
    case 'instagram-age-distribution':
    case 'age':
      return 'Age Distribution';
    case 'instagram-gender-distribution':
    case 'gender':
      return 'Gender Distribution';
    case 'youtube-gender-distribution':
      return 'YouTube Gender Distribution';
    case 'downloads':
      return 'Downloads';
    default:
      return id
        .replace(/-/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
  }
}

function renderSummaryPreview(
  _widgetId: string,
  onOpenDetails: () => void
): React.ReactNode {
  return (
    <div className="mobile-summary-wrap">
      <button
        type="button"
        className="mobile-summary-tap-target mobile-summary-text-only"
        onClick={onOpenDetails}
      >
        <span className="mobile-summary-cta-icon" aria-hidden>
          ↗
        </span>
        <span className="mobile-summary-cta-title">Tap to view chart</span>
        <span className="mobile-summary-cta-subtitle">Open full chart details</span>
      </button>
    </div>
  );
}

const MobileDashboard: React.FC<MobileDashboardProps> = ({
  dateRange,
  onDateRangeChange,
  reports = [],
  onToggleReport,
  analyticsActionsEnabled = false,
  platformArtistId,
  platformMappingLoading = false,
  platformMappingError = null,
  userPrimaryArtistId,
}) => {
  const { currentArtistId } = useArtist();
  const artistId = userPrimaryArtistId || currentArtistId?.toString();
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isReportPickerOpen, setIsReportPickerOpen] = useState(false);
  const [detailsWidgetId, setDetailsWidgetId] = useState<string | null>(null);

  const visibleWidgetIds = reports
    .filter((report) => report.isVisible)
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((report) => report.id);

  const fallbackWidgetIds = [
    'weekly-growth',
    'monthly-growth',
    'age',
    'gender',
    'engagement',
    'funnel',
  ];

  const widgetIdsToRender =
    visibleWidgetIds.length > 0 ? visibleWidgetIds : fallbackWidgetIds;

  function renderWidget(id: string): React.ReactNode {
    switch (id) {
      case 'monthly-listeners':
      case 'revenue-streaming-performance':
        return <MonthlyListenersChart dateRange={dateRange} />;
      case 'monthly-followers':
        return <MonthlyFollowersChart dateRange={dateRange} />;
      case 'editorial-playlists':
      case 'editorial-playlist-reach':
        return (
          <EditorialPlaylistMixedChart
            platformArtistId={platformArtistId}
            artistId={artistId}
          />
        );
      case 'platforms':
      case 'total-playlist-count':
        return (
          <TotalPlaylistMixedChart
            platformArtistId={platformArtistId}
            artistId={artistId}
          />
        );
      case 'brand-affinity':
        return (
          <BrandAffinityChart
            artistId={artistId}
            platformArtistId={platformArtistId}
            platformMappingLoading={platformMappingLoading}
            platformMappingError={platformMappingError}
          />
        );
      case 'platform-growth-comparison':
        // Match desktop tile: no artist_id query param so API returns the same default dataset.
        return <PlatformGrowthComparisonChart />;
      case 'geography':
      case 'geographic-distribution':
        return <GeographicDistributionBarChart artistId={artistId} />;
      case 'weekly-growth':
        return <WeeklyGrowthChart artistId={undefined} />;
      case 'monthly-growth':
        return <MonthlyGrowthChart artistId={undefined} />;
      case 'engagement-rates':
      case 'engagement':
        return <EngagementRatesChart artistId={artistId} />;
      case 'spotify-funnel':
      case 'funnel':
        return <FunnelChart artistId={artistId} dateRange={dateRange} />;
      case 'instagram-age-distribution':
      case 'age':
        return <InstagramAgeDistributionChart artistId={artistId} />;
      case 'instagram-gender-distribution':
      case 'gender':
        return <InstagramGenderDistributionChart artistId={artistId} />;
      case 'youtube-gender-distribution':
        return <YouTubeGenderDistributionChart artistId={artistId} />;
      case 'downloads':
        return <AudienceReachPyramidChart artistId={artistId} />;
      default:
        return (
          <div className="w-full h-full flex items-center justify-center text-sm text-gray-500">
            Report not available
          </div>
        );
    }
  }

  return (
    <div className="mobile-dashboard">
      <MobileHighlights />

      <div className="mobile-analytics-section">
        <div className="mobile-analytics-header">
          <div className="mobile-analytics-title">Analytics</div>
        </div>

        <div className="mobile-analytics-controls">
          <MobileFilterBar
            dateRange={dateRange}
            onDateClick={() => setIsDatePickerOpen(true)}
            onMenuClick={() => setIsMenuOpen(true)}
          />
        </div>

        {isDatePickerOpen && (
          <>
            <div
              className="mobile-date-picker-backdrop"
              role="presentation"
              onClick={() => setIsDatePickerOpen(false)}
            />
            <div className="mobile-date-picker-panel">
              <DateRangePicker
                dateRange={dateRange}
                onDateRangeChange={onDateRangeChange}
                open={isDatePickerOpen}
                onOpenChange={setIsDatePickerOpen}
                hideTrigger
              />
            </div>
          </>
        )}

        {isMenuOpen && (
          <>
            <div
              className="mobile-menu-backdrop"
              role="presentation"
              onClick={() => setIsMenuOpen(false)}
            />
            <div className="mobile-menu-sheet">
              <button
                type="button"
                className="mobile-menu-item mobile-report-button"
                disabled={!analyticsActionsEnabled || !onToggleReport}
                onClick={() => {
                  if (!analyticsActionsEnabled || !onToggleReport) {
                    return;
                  }
                  setIsMenuOpen(false);
                  setIsReportPickerOpen(true);
                }}
              >
                Add / Remove Reports
              </button>
            </div>
          </>
        )}

        {onToggleReport && (
          <ReportCustomizationDropdown
            variant="sheet"
            open={isReportPickerOpen}
            onOpenChange={setIsReportPickerOpen}
            reports={reports}
            onToggleReport={onToggleReport}
          />
        )}

        {detailsWidgetId && (
          <>
            <div
              className="mobile-revenue-details-backdrop"
              role="presentation"
              onClick={() => setDetailsWidgetId(null)}
            />
            <div className="mobile-revenue-details-sheet">
              <div className="mobile-revenue-details-header">
                <div className="mobile-revenue-details-title">
                  {getWidgetTitle(detailsWidgetId)}
                </div>
              </div>
              <div className="mobile-revenue-details-chart">
                <div className="mobile-chart-details-content">
                  {renderWidget(detailsWidgetId)}
                </div>
              </div>
              <button
                type="button"
                className="mobile-revenue-details-close mobile-revenue-details-close-fixed"
                onClick={() => setDetailsWidgetId(null)}
              >
                Close
              </button>
            </div>
          </>
        )}

        <div className="mobile-charts-list">
          {widgetIdsToRender.map((widgetId, index) => (
            <MobileChartCard
              key={`${widgetId}-${index}`}
              title={getWidgetTitle(widgetId)}
              onViewDetails={() => setDetailsWidgetId(widgetId)}
              containerClassName="mobile-chart-container-summary"
            >
              {renderSummaryPreview(widgetId, () => setDetailsWidgetId(widgetId))}
            </MobileChartCard>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MobileDashboard;
